import { Button, Checkbox, Divider, Drawer, Form, Input, message } from 'antd';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

// 自定义样式组件
const StyledForm = styled(Form)`
  .ant-form-item-label > label {
    color: #333333;
    font-weight: 500;
  }

  .ant-input {
    border-radius: 8px;
    border: 1px solid #e8e8e8;
    box-shadow: none;
    transition: all 0.3s;

    &:hover,
    &:focus {
      border-color: ${(props) => props.$themeColor || '#AD30E5'};
      box-shadow: 0 0 0 2px rgba(${(props) => props.$rgbColor || '173, 48, 229'}, 0.1);
    }
  }

  .ant-input-textarea {
    border-radius: 8px;

    .ant-input {
      border-radius: 8px;
    }
  }

  .ant-checkbox-wrapper {
    &:hover {
      .ant-checkbox-inner {
        border-color: ${(props) => props.$themeColor || '#AD30E5'};
      }
    }
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: ${(props) => props.$themeColor || '#AD30E5'};
    border-color: ${(props) => props.$themeColor || '#AD30E5'};
  }

  .ant-form-item {
    margin-bottom: 16px;
  }

  .ant-btn-primary {
    background: ${(props) => props.$themeColor || '#AD30E5'};
    border-color: ${(props) => props.$themeColor || '#AD30E5'};
    border-radius: 8px;
    height: 40px;
    box-shadow: 0 2px 6px rgba(${(props) => props.$rgbColor || '173, 48, 229'}, 0.4);
    transition: all 0.3s;

    &:hover,
    &:focus {
      background: ${(props) => props.$hoverColor || '#C14CFF'};
      border-color: ${(props) => props.$hoverColor || '#C14CFF'};
      box-shadow: 0 4px 12px rgba(${(props) => props.$rgbColor || '173, 48, 229'}, 0.6);
    }
  }
`;

// 自定义按钮样式
const FloatingButton = styled.button`
  position: fixed;
  z-index: 99999;
  right: ${(props) => props.$position?.right}px;
  bottom: ${(props) => props.$position?.bottom}px;
  cursor: ${(props) => (props.$isDragging ? 'grabbing' : 'grab')};
  border-radius: 10px;
  padding: 8px 15px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
  background: ${(props) => props.$themeColor || '#AD30E5'};
  color: #fff;
  border: 2px solid transparent;
  transition: all 0.3s;
  font-weight: 500;
  font-size: 14px;
  &:hover {
    box-shadow: 0 5px 15px rgba(${(props) => props.$rgbColor || '173, 48, 229'}, 0.5);
    border: 2px solid #fff;
  }
`;

// 自定义抽屉样式
const StyledDrawer = styled(Drawer)`
  .ant-drawer-header {
    background: ${(props) => props.$themeColor || '#AD30E5'};
    border-bottom: none;
  }

  .ant-drawer-title {
    color: #ffffff;
    font-weight: 500;
  }

  .ant-drawer-close {
    color: #ffffff;

    &:hover {
      color: rgba(255, 255, 255, 0.8);
    }
  }

  .ant-drawer-body {
    padding: 24px;
  }
`;

const DraggableButton = ({ onClick, style = {}, children, device }) => {
  const buttonRef = useRef(null);
  const [position, setPosition] = useState({ right: 20, bottom: 80 });
  const draggingRef = useRef(false);
  const offsetRef = useRef({ x: 0, y: 0 });
  const [drawerVisible, setDrawerVisible] = useState(false);
  const startPosRef = useRef({ x: 0, y: 0 });
  const moveDistanceRef = useRef(0);
  const [form] = Form.useForm();

  // 获取系统主题色
  const [themeColor, setThemeColor] = useState('#AD30E5');
  const [rgbColor, setRgbColor] = useState('173, 48, 229');
  const [hoverColor, setHoverColor] = useState('#C14CFF');

  // 这个声明有一个潜在问题：如果 localStorage.getItem('configTool') 返回的是一个格式错误的 JSON 字符串，JSON.parse 会抛出异常，导致组件渲染报错。建议加 try-catch 处理异常，或者先判断 localStorage.getItem('configTool') 是否为有效 JSON。
  const [configForm, setConfigForm] = useState(() => {
    let localConfig = null;
    try {
      const item = localStorage.getItem('configTool');
      if (item) {
        localConfig = JSON.parse(item);
      }
    } catch (e) {
      // 解析失败，忽略本地存储
      localConfig = null;
    }
    return (
      localConfig || {
        un: '',
        ue: '',
        m: '',
        um: '',
        country: '',
        w: '',
        h: '',
        fullScreen: false,
        singleChat: false,
        showEndCustomerName: false,
        showOnlineMediaForVIP: false,
        noHeader: false,
        mobileEnterSendMessage: false,
        useNewMobileChat: false,
        mobileIconPosition: false,
        ticketAttr: '',
        languageCode: '',
        faqUseDeviceLanguage: false,
        showToAgentName: true,
      }
    );
  });

  // 尝试从localStorage获取主题色
  useEffect(() => {
    try {
      // 尝试获取系统主题色
      const storedConfig = localStorage.getItem('configTool');
      if (storedConfig) {
        const parsedConfig = JSON.parse(storedConfig);
        const color = parsedConfig.chatThemeColor || '#AD30E5';
        setThemeColor(color);

        // 转换为RGB格式以便于透明度处理
        const hexToRgb = (hex) => {
          const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
          return result
            ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
            : '173, 48, 229';
        };

        setRgbColor(hexToRgb(color));

        // 生成悬停色（稍微亮一些的颜色）
        const lightenColor = (color, percent) => {
          const num = parseInt(color.replace('#', ''), 16),
            amt = Math.round(2.55 * percent),
            R = (num >> 16) + amt,
            G = ((num >> 8) & 0x00ff) + amt,
            B = (num & 0x0000ff) + amt;
          return `#${(
            0x1000000 +
            (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
            (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
            (B < 255 ? (B < 1 ? 0 : B) : 255)
          )
            .toString(16)
            .slice(1)}`;
        };

        setHoverColor(lightenColor(color, 15));
      }
    } catch (e) {
      console.error('获取主题色失败', e);
    }
  }, []);

  // 拖动中
  const handleDragging = (e) => {
    if (!draggingRef.current) return;
    let clientX, clientY;
    if (e.type === 'touchmove') {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    // 记录移动距离
    moveDistanceRef.current = Math.max(
      moveDistanceRef.current,
      Math.abs(clientX - startPosRef.current.x),
      Math.abs(clientY - startPosRef.current.y)
    );
    const right = window.innerWidth - clientX + offsetRef.current.x;
    const bottom = window.innerHeight - clientY + offsetRef.current.y;
    setPosition({ right, bottom });
    if (e.type === 'touchmove') e.preventDefault();
  };

  // 拖动结束
  const handleDragEnd = (e) => {
    draggingRef.current = false;
    document.removeEventListener('mousemove', handleDragging);
    document.removeEventListener('mouseup', handleDragEnd);
    document.removeEventListener('touchmove', handleDragging);
    document.removeEventListener('touchend', handleDragEnd);
    // 鼠标/手指抬起时判断是否为点击
    let clientX, clientY;
    if (e && e.type && e.type.startsWith('touch')) {
      clientX = e.changedTouches[0].clientX;
      clientY = e.changedTouches[0].clientY;
    } else if (e) {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    // 如果移动距离很小，判定为点击
    if (moveDistanceRef.current <= 5) {
      setDrawerVisible(!drawerVisible);
      if (onClick) onClick(e);
    }
    moveDistanceRef.current = 0;
  };

  // 鼠标/触摸按下
  const handleDragStart = (e) => {
    e.preventDefault();
    draggingRef.current = true;
    let clientX, clientY;
    if (e.type === 'touchstart') {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    startPosRef.current = { x: clientX, y: clientY };
    moveDistanceRef.current = 0;
    const rect = buttonRef.current.getBoundingClientRect();
    offsetRef.current = {
      x: clientX - rect.right,
      y: clientY - rect.bottom,
    };
    document.addEventListener('mousemove', handleDragging);
    document.addEventListener('mouseup', handleDragEnd);
    document.addEventListener('touchmove', handleDragging, { passive: false });
    document.addEventListener('touchend', handleDragEnd);
  };

  useEffect(() => {
    // 组件卸载时清理
    return () => {
      document.removeEventListener('mousemove', handleDragging);
      document.removeEventListener('mouseup', handleDragEnd);
      document.removeEventListener('touchmove', handleDragging);
      document.removeEventListener('touchend', handleDragEnd);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <div>
      {!drawerVisible && (
        <FloatingButton
          ref={buttonRef}
          $position={position}
          $isDragging={draggingRef.current}
          $themeColor={themeColor}
          $rgbColor={rgbColor}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          // 自定义样式合并
          style={{ ...style }}
        >
          ConfigTools
        </FloatingButton>
      )}
      <StyledDrawer
        title="配置工具"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={device == 'pc' ? '50%' : '100%'}
        $themeColor={themeColor}
      >
        <div>
          <StyledForm
            form={form}
            layout={device == 'pc' ? 'horizontal' : 'vertical'}
            initialValues={configForm}
            $themeColor={themeColor}
            $rgbColor={rgbColor}
            $hoverColor={hoverColor}
            onFinish={(values) => {
              // 这里可以处理表单提交逻辑
              console.log('表单提交数据:', values);
              // 循环遍历values对象的所有键值对
              let mobileIconPosition = {
                top: values.mobileIconPositionTop ? values.mobileIconPositionTop : 'auto',
                right: values.mobileIconPositionRight ? values.mobileIconPositionRight : 'auto',
                bottom: values.mobileIconPositionBottom ? values.mobileIconPositionBottom : 'auto',
                left: values.mobileIconPositionLeft ? values.mobileIconPositionLeft : 'auto',
              };
              Object.keys(values).forEach((key) => {
                if (values[key] === undefined || values[key] === null || values[key] === false) {
                  if (
                    key === 'un' ||
                    key === 'ue' ||
                    key === 'm' ||
                    key === 'um' ||
                    key === 'country' ||
                    key === 'languageCode' ||
                    key === 'mobileIconPositionTop' ||
                    key === 'mobileIconPositionRight' ||
                    key === 'mobileIconPositionBottom' ||
                    key === 'mobileIconPositionLeft' ||
                    key === 'w' ||
                    key === 'h'
                  ) {
                    values[key] = '';
                  } else {
                    values[key] = false;
                  }
                }
                if (key === 'ticketAttr') {
                  values[key] = values[key].replace(/\n/g, '');
                }
              });
              if (values.ticketAttr === '') {
                values.ticketAttr = '{}';
              }
              values.mobileIconPosition = mobileIconPosition;
              localStorage.setItem('configTool', JSON.stringify(values));
              // 显示提示消息
              message.success('配置已保存，即将刷新页面');
              // 3秒后刷新页面
              setTimeout(() => {
                window.location.reload();
              }, 2000);
            }}
          >
            <Form.Item label="userName" name="un">
              <Input placeholder="请输入用户名称" />
            </Form.Item>
            <Form.Item label="userEmail" name="ue">
              <Input placeholder="请输入用户邮箱" />
            </Form.Item>
            <Form.Item label="memberId" name="m">
              <Input placeholder="请输入用户ID" />
            </Form.Item>
            <Form.Item label="userMobile" name="um">
              <Input placeholder="请输入用户手机" />
            </Form.Item>
            <Form.Item label="country" name="country">
              <Input placeholder="请输入国家/地区" />
            </Form.Item>
            <Form.Item label="containerW" name="w">
              <Input placeholder="请输入容器宽度" />
            </Form.Item>
            <Form.Item label="containerH" name="h">
              <Input placeholder="请输入容器高度" />
            </Form.Item>

            <Divider style={{ margin: '8px 0 16px' }} />

            <Form.Item label="fullScreen" name="fullScreen" valuePropName="checked">
              <Checkbox></Checkbox>
            </Form.Item>
            <Form.Item label="singleChat" name="singleChat" valuePropName="checked">
              <Checkbox></Checkbox>
            </Form.Item>
            <Form.Item label="showEndCustomerName" name="showEndCustomerName" valuePropName="checked">
              <Checkbox></Checkbox>
            </Form.Item>
            <Form.Item label="showOnlineMediaForVIP" name="showOnlineMediaForVIP" valuePropName="checked">
              <Checkbox></Checkbox>
            </Form.Item>
            <Form.Item label="noHeader" name="noHeader" valuePropName="checked">
              <Checkbox></Checkbox>
            </Form.Item>
            <Form.Item label="mobileEnterSendMessage" name="mobileEnterSendMessage" valuePropName="checked">
              <Checkbox></Checkbox>
            </Form.Item>

            <Divider style={{ margin: '8px 0 16px' }} />
            <Form.Item label="useNewMobileChat" name="useNewMobileChat" valuePropName="checked">
              <Checkbox></Checkbox>
            </Form.Item>
            <Form.Item label="mobileIconPositionTop" name="mobileIconPositionTop">
              <Input placeholder="例如: 10px" />
            </Form.Item>
            <Form.Item label="mobileIconPositionRight" name="mobileIconPositionRight">
              <Input placeholder="例如: 10px" />
            </Form.Item>
            <Form.Item label="mobileIconPositionBottom" name="mobileIconPositionBottom">
              <Input placeholder="例如: 10px" />
            </Form.Item>
            <Form.Item label="mobileIconPositionLeft" name="mobileIconPositionLeft">
              <Input placeholder="例如: 10px" />
            </Form.Item>

            <Divider style={{ margin: '8px 0 16px' }} />

            <Form.Item label="ticketAttr" name="ticketAttr">
              <Input.TextArea placeholder="请输入JSON格式的工单属性" rows={3} />
            </Form.Item>
            <Form.Item label="languageCode" name="languageCode">
              <Input placeholder="例如: zh-CN, en-US" />
            </Form.Item>
            {/* <Form.Item label="faqUseDeviceLanguage" name="faqUseDeviceLanguage" valuePropName="checked">
              <Checkbox></Checkbox>
            </Form.Item> */}
            <Form.Item label="showToAgentName" name="showToAgentName" valuePropName="checked">
              <Checkbox></Checkbox>
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" style={{ width: '100%' }}>
                Submit
              </Button>
            </Form.Item>
          </StyledForm>
        </div>
      </StyledDrawer>
    </div>
  );
};

export default DraggableButton;
